import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { getApiErrorMessage, inr, todayISO, formatDate } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronRight, Pencil, Printer, Search } from "lucide-react";
import PurchasePreviewDialog from "@/components/PurchasePreviewDialog";
import MedicineTypeahead from "@/components/MedicineTypeahead";
import MedicineMasterForm from "@/components/MedicineMasterForm";

const gstOptions = [0, 5, 12, 18, 28];

const emptyItem = () => ({
  medicine_id: "",
  medicine_name: "",
  batch_no: "",
  expiry_date: "",
  quantity: "",
  purchase_price: 0,
  mrp: 0,
  gst_percent: 12,
  master_gst_percent: 12,
  update_master_gst: false,
  current_stock: 0,
  product_type: "",
  packing_unit: "",
  base_unit: "",
  units_per_pack: 1,
  allowed_selling_units: [],
  price_calc_method: "automatic",
  base_unit_selling_price: 0,
  base_unit_purchase_price: 0,
});

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editId, setEditId] = useState(null);
  const [previewPurchase, setPreviewPurchase] = useState(null);
  const [shopInfo, setShopInfo] = useState({});
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickIdx, setQuickIdx] = useState(null);
  const [quickInitialName, setQuickInitialName] = useState("");
  const [supplierQuickOpen, setSupplierQuickOpen] = useState(false);
  const [supplierQuickForm, setSupplierQuickForm] = useState({
    name: "", contact_person: "", phone: "", email: "", gstin: "", address: ""
  });
  const [supplierQuickSaving, setSupplierQuickSaving] = useState(false);

  const [form, setForm] = useState({
    supplier_id: "",
    supplier_name: "",
    invoice_no: "",
    invoice_date: todayISO(),
    notes: "",
    items: [emptyItem()],
  });

  const load = async () => {
    const [p, s, m] = await Promise.all([
      api.get("/purchases"),
      api.get("/suppliers"),
      api.get("/medicines", { params: { limit: 500 } }),
    ]);
    setPurchases(p.data);
    setSuppliers(s.data);
    setMedicines(m.data);
  };

  useEffect(() => {
    load();
    api.get("/settings/shop").then((r) => setShopInfo(r.data)).catch(() => { });
  }, []);

  const filteredPurchases = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return purchases;
    return purchases.filter((p) => {
      const invoice = (p.invoice_no || "").toString().toLowerCase();
      const supplier = (p.supplier_name || "").toString().toLowerCase();
      return invoice.includes(term) || supplier.includes(term);
    });
  }, [purchases, searchQuery]);

  const totals = form.items.reduce(
    (acc, it) => {
      const line = Number(it.purchase_price) * Number(it.quantity);
      const tax = line * (Number(it.gst_percent) / 100);
      acc.subtotal += line;
      acc.tax += tax;
      return acc;
    },
    { subtotal: 0, tax: 0 }
  );
  const total = totals.subtotal + totals.tax;

  const withMedicineMasterMetadata = (item, med) => ({
    ...item,
    medicine_id: med.id,
    medicine_name: med.name,
    purchase_price: med.purchase_price || 0,
    mrp: med.mrp || 0,
    gst_percent: med.gst_percent ?? 12,
    master_gst_percent: med.gst_percent ?? 12,
    update_master_gst: false,
    current_stock: med.stock || 0,
    product_type: med.product_type || "",
    packing_unit: med.packing_unit || med.unit || "",
    base_unit: med.base_unit || "",
    units_per_pack: med.units_per_pack || 1,
    allowed_selling_units: med.allowed_selling_units || [],
    price_calc_method: med.price_calc_method || "automatic",
    base_unit_selling_price: med.base_unit_selling_price || 0,
    base_unit_purchase_price: med.base_unit_purchase_price || 0,
  });

  const purchaseQtyLabel = (item) => `Qty (${item.packing_unit || "Pack"})`;

  const buildMedicineMasterPayload = (med, gstPercent) => ({
    name: med.name || "",
    generic_name: med.generic_name || "",
    product_type: med.product_type || "General Product",
    manufacturer: med.manufacturer || "",
    hsn_code: med.hsn_code || "",
    category: med.category || "",
    unit: med.packing_unit || med.unit || "Piece",
    mrp: Number(med.mrp || 0),
    purchase_price: Number(med.purchase_price || 0),
    gst_percent: Number(gstPercent),
    low_stock_threshold: Number(med.low_stock_threshold ?? 10),
    packing_unit: med.packing_unit || med.unit || "Piece",
    base_unit: med.base_unit || "Piece",
    units_per_pack: Number(med.units_per_pack || 1),
    allowed_selling_units: med.allowed_selling_units || [med.packing_unit || med.unit || "Piece"],
    price_calc_method: med.price_calc_method || "automatic",
    base_unit_selling_price: med.base_unit_selling_price,
    base_unit_purchase_price: med.base_unit_purchase_price,
  });

  const updateItem = (idx, key, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: val };
    if (key === "medicine_id") {
      const med = medicines.find((m) => m.id === val);
      if (med) {
        items[idx] = withMedicineMasterMetadata(items[idx], med);
      }
    }
    setForm({ ...form, items });
  };

  const submit = async () => {
    if (!form.invoice_no.trim()) return toast.error("Invoice number required");
    const items = form.items.filter((i) => i.medicine_id && i.quantity > 0);
    if (items.length === 0) return toast.error("Add at least one item");

    const supplier = suppliers.find((s) => s.id === form.supplier_id);
    const payload = {
      ...form,
      supplier_name: supplier?.name || form.supplier_name || "",
      items: items.map((i) => ({
        ...i,
        quantity: Number(i.quantity),
        purchase_price: Number(i.purchase_price),
        mrp: Number(i.mrp),
        gst_percent: Number(i.gst_percent),
        expiry_date: i.expiry_date || null,
      })),
    };
    try {
      if (editId) {
        await api.put(`/purchases/${editId}`, payload);
        toast.success("Purchase updated — stock adjusted");
      } else {
        await api.post("/purchases", payload);
        toast.success("Purchase recorded — stock updated");
      }

      const masterGstUpdates = items.filter((i) => i.update_master_gst && Number(i.gst_percent) !== Number(i.master_gst_percent));
      if (masterGstUpdates.length > 0) {
        await Promise.all(masterGstUpdates.map((i) => {
          const med = medicines.find((m) => m.id === i.medicine_id);
          if (!med) return Promise.resolve();
          return api.put(`/medicines/${i.medicine_id}`, buildMedicineMasterPayload(med, i.gst_percent));
        }));
      }
      setOpen(false);
      setEditId(null);
      setForm({ supplier_id: "", supplier_name: "", invoice_no: "", invoice_date: todayISO(), notes: "", items: [emptyItem()] });
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      supplier_id: p.supplier_id || "",
      supplier_name: p.supplier_name || "",
      invoice_no: p.invoice_no,
      invoice_date: p.invoice_date,
      notes: p.notes || "",
      items: p.items.map((it) => {
        const med = medicines.find((m) => m.id === it.medicine_id);
        const preserved = {
          ...emptyItem(),
          ...it,
          batch_no: it.batch_no || "",
          expiry_date: it.expiry_date || "",
          quantity: it.quantity ?? "",
        };
        return med ? withMedicineMasterMetadata(preserved, med) : preserved;
      }),
    });
    setOpen(true);
  };

  const openNew = () => {
    setEditId(null);
    setForm({ supplier_id: "", supplier_name: "", invoice_no: "", invoice_date: todayISO(), notes: "", items: [emptyItem()] });
    setOpen(true);
  };

  const openQuickAdd = (idx, initialName) => {
    setQuickIdx(idx);
    setQuickInitialName((initialName || "").trim());
    setQuickOpen(true);
  };

  const handleQuickMedicineSaved = (created) => {
    setMedicines((m) => [created, ...m]);
    if (quickIdx != null) {
      const items = [...form.items];
      items[quickIdx] = {
        ...withMedicineMasterMetadata(items[quickIdx], created),
        quantity: items[quickIdx].quantity ?? "",
      };
      setForm({ ...form, items });
    }
    toast.success(`"${created.name}" added to catalogue`);
    setQuickOpen(false);
  };

  const openSupplierQuickAdd = () => {
    setSupplierQuickForm({
      name: "", contact_person: "", phone: "", email: "", gstin: "", address: ""
    });
    setSupplierQuickOpen(true);
  };

  const saveSupplierQuickAdd = async () => {
    if (!supplierQuickForm.name.trim()) return toast.error("Supplier name required");
    setSupplierQuickSaving(true);
    try {
      const { data: created } = await api.post("/suppliers", supplierQuickForm);
      toast.success(`Supplier "${created.name}" created`);
      setSuppliers((s) => [...s, created].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((f) => ({ ...f, supplier_id: created.id }));
      setSupplierQuickOpen(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSupplierQuickSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Stock In</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Purchases</h1>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="purchases-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search invoice or supplier..."
              className="pl-9 w-64 bg-white"
            />
          </div>
          <Button onClick={openNew} data-testid="new-purchase-btn">
            <Plus size={16} className="mr-1" /> New Purchase
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Items</TableHead>
              <TableHead className="text-right">Subtotal</TableHead>
              <TableHead className="text-right">Tax</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPurchases.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No purchases found</TableCell>
              </TableRow>
            )}
            {filteredPurchases.map((p) => (
              <React.Fragment key={p.id}>
                <TableRow className="data-row cursor-pointer" onClick={() => setExpanded({ ...expanded, [p.id]: !expanded[p.id] })}>
                  <TableCell>{expanded[p.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</TableCell>
                  <TableCell className="font-mono text-xs">{p.invoice_no}</TableCell>
                  <TableCell>{p.supplier_name || "—"}</TableCell>
                  <TableCell className="text-xs">{formatDate(p.invoice_date)}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.items?.length || 0}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(p.subtotal)}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(p.tax_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{inr(p.total)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`edit-purchase-${p.id}`}
                      className="p-1 hover:bg-muted rounded"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => setPreviewPurchase(p)}
                      data-testid={`print-purchase-${p.id}`}
                      className="p-1 hover:bg-muted rounded ml-1"
                      title="Print"
                    >
                      <Printer size={14} />
                    </button>
                  </TableCell>
                </TableRow>
                {expanded[p.id] && (
                  <TableRow>
                    <TableCell colSpan={9} className="bg-muted/40">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Line items</div>
                      <div className="space-y-1">
                        {p.items.map((it, i) => (
                          <div key={i} className="grid grid-cols-6 gap-2 text-sm tabular-nums">
                            <div className="col-span-2">{it.medicine_name}</div>
                            <div>Qty: {it.quantity}</div>
                            <div>@ {inr(it.purchase_price)}</div>
                            <div>GST {it.gst_percent}%</div>
                            <div className="text-right">{inr(it.purchase_price * it.quantity)}</div>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-6xl" data-testid="purchase-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Purchase" : "New Purchase"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Supplier</Label>
              <div className="flex gap-1.5 mt-0.5">
                <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger data-testid="supplier-select" className="flex-1"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={openSupplierQuickAdd}
                  title="Add Supplier"
                  data-testid="quick-add-supplier-btn"
                >
                  <Plus size={16} />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Invoice No. *</Label>
              <Input data-testid="invoice-no" value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Invoice Date</Label>
              <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
            </div>
          </div>

          <div className="mt-4 border border-border rounded-md">
            <div className="grid gap-2 px-3 py-2 border-b border-border bg-muted text-xs uppercase tracking-widest text-muted-foreground [grid-template-columns:minmax(220px,2.4fr)_minmax(120px,1fr)_minmax(145px,1.1fr)_minmax(95px,.75fr)_minmax(95px,.75fr)_minmax(95px,.75fr)_minmax(180px,1.4fr)_40px]">
              <div>Medicine</div>
              <div>Batch</div>
              <div>Expiry</div>
              <div className="text-right">Qty</div>
              <div className="text-right">Cost</div>
              <div className="text-right">MRP</div>
              <div className="text-right">GST%</div>
              <div></div>
            </div>
            {form.items.map((it, idx) => (
              <div key={idx} className="grid gap-2 px-3 py-2 border-b border-border items-start [grid-template-columns:minmax(220px,2.4fr)_minmax(120px,1fr)_minmax(145px,1.1fr)_minmax(95px,.75fr)_minmax(95px,.75fr)_minmax(95px,.75fr)_minmax(180px,1.4fr)_40px]">
                <div>
                  <MedicineTypeahead
                    value={it.medicine_id}
                    valueLabel={it.medicine_name}
                    medicines={medicines}
                    onSelect={(m) => updateItem(idx, "medicine_id", m.id)}
                    onAddNew={(q) => openQuickAdd(idx, q)}
                    testId={`purchase-med-${idx}`}
                  />
                  {it.medicine_id && (
                    <div className="text-[10px] text-muted-foreground mt-1 min-h-[16px] leading-4">
                      Current stock: {it.current_stock ?? 0} {it.base_unit || "units"}
                    </div>
                  )}
                </div>
                <div><Input className="h-9" value={it.batch_no} onChange={(e) => updateItem(idx, "batch_no", e.target.value)} /></div>
                <div><Input className="h-9" type="date" value={it.expiry_date || ""} onChange={(e) => updateItem(idx, "expiry_date", e.target.value)} /></div>
                <div>
                  <Label className="sr-only">{purchaseQtyLabel(it)}</Label>
                  <Input
                    type="number"
                    className="h-9 text-right"
                    title={purchaseQtyLabel(it)}
                    placeholder={purchaseQtyLabel(it)}
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                  />
                  {it.medicine_id && (
                    <div className="text-[10px] text-right text-muted-foreground mt-1 min-h-[16px] leading-4 whitespace-nowrap">
                      {purchaseQtyLabel(it)}
                    </div>
                  )}
                </div>
                <div><Input type="number" step="0.01" className="h-9 text-right" value={it.purchase_price} onChange={(e) => updateItem(idx, "purchase_price", e.target.value)} /></div>
                <div><Input type="number" step="0.01" className="h-9 text-right" value={it.mrp} onChange={(e) => updateItem(idx, "mrp", e.target.value)} /></div>
                <div>
                  <Select value={String(it.gst_percent)} onValueChange={(v) => updateItem(idx, "gst_percent", Number(v))}>
                    <SelectTrigger className="h-9 text-right"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {gstOptions.map((gst) => <SelectItem key={gst} value={String(gst)}>{gst}%</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {it.medicine_id && Number(it.gst_percent) !== Number(it.master_gst_percent) && (
                    <div className="mt-1 text-[10px] leading-4 text-amber-700">
                      <div>⚠ GST differs from the Medicine Master.</div>
                      <label className="flex items-start gap-1 text-muted-foreground">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={!!it.update_master_gst}
                          onChange={(e) => updateItem(idx, "update_master_gst", e.target.checked)}
                        />
                        <span>Update Medicine Master GST for future purchases</span>
                      </label>
                    </div>
                  )}
                </div>
                <div className="h-9 flex items-center justify-end">
                  <button onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} className="text-destructive p-1 hover:bg-muted rounded">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
            <div className="px-3 py-2">
              <Button variant="outline" size="sm" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })} data-testid="add-purchase-line">
                <Plus size={14} className="mr-1" /> Add Item
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-2">
            <div className="col-span-2 text-xs text-muted-foreground">
              Purchases record cost, tax and update stock levels & pricing.
            </div>
            <div className="rounded-md border border-border p-3 space-y-1 text-sm tabular-nums">
              <div className="flex justify-between"><span>Subtotal</span><span>{inr(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span>Tax</span><span>{inr(totals.tax)}</span></div>
              <div className="flex justify-between font-semibold pt-1 border-t border-border"><span>Total</span><span>{inr(total)}</span></div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditId(null); }}>Cancel</Button>
            <Button onClick={submit} data-testid="save-purchase">{editId ? "Update Purchase" : "Save Purchase"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PurchasePreviewDialog
        open={!!previewPurchase}
        onOpenChange={(o) => !o && setPreviewPurchase(null)}
        purchase={previewPurchase}
        shop={shopInfo}
      />

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <MedicineMasterForm
          defaultName={quickInitialName}
          showStock={false}
          showPurchaseSpecificFields={false}
          requireHsn={true}
          title="Add New Medicine"
          submitLabel="Save & Use"
          onCancel={() => setQuickOpen(false)}
          onSaved={handleQuickMedicineSaved}
        />
      </Dialog>

      {/* Quick-add supplier from purchase form */}
      <Dialog open={supplierQuickOpen} onOpenChange={setSupplierQuickOpen}>
        <DialogContent className="max-w-xl" data-testid="supplier-quick-add-dialog">
          <DialogHeader>
            <DialogTitle>Add New Supplier</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <QF label="Name *">
                <Input
                  data-testid="supplier-quick-name"
                  value={supplierQuickForm.name}
                  onChange={(e) => setSupplierQuickForm({ ...supplierQuickForm, name: e.target.value })}
                  autoFocus
                />
              </QF>
            </div>
            <QF label="Contact Person">
              <Input
                value={supplierQuickForm.contact_person}
                onChange={(e) => setSupplierQuickForm({ ...supplierQuickForm, contact_person: e.target.value })}
              />
            </QF>
            <QF label="Phone">
              <Input
                value={supplierQuickForm.phone}
                onChange={(e) => setSupplierQuickForm({ ...supplierQuickForm, phone: e.target.value })}
              />
            </QF>
            <QF label="Email">
              <Input
                value={supplierQuickForm.email}
                onChange={(e) => setSupplierQuickForm({ ...supplierQuickForm, email: e.target.value })}
                type="email"
              />
            </QF>
            <QF label="GSTIN">
              <Input
                value={supplierQuickForm.gstin}
                onChange={(e) => setSupplierQuickForm({ ...supplierQuickForm, gstin: e.target.value })}
              />
            </QF>
            <div className="col-span-2">
              <QF label="Address">
                <Input
                  value={supplierQuickForm.address}
                  onChange={(e) => setSupplierQuickForm({ ...supplierQuickForm, address: e.target.value })}
                />
              </QF>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierQuickOpen(false)}>Cancel</Button>
            <Button onClick={saveSupplierQuickAdd} disabled={supplierQuickSaving} data-testid="supplier-quick-save">
              {supplierQuickSaving ? "Saving…" : "Save Supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QF({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
